import { PolicyList, Input, Typography, StatusBox } from 'iglooform';
import { IPolicyCardProps } from 'iglooform/es/policy-list/policy-card';
import { SearchOutlined, PhoneOutlined } from 'iglooicon';
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
} from 'react';
import { Spin } from 'antd';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import {
  RoleMessages,
  UserStatusMessages,
  UserStatusTypes,
} from '@/utils/role';
import { getDownlines } from '../service';
import Context from '@/utils/context';
import { handleOnScroll } from '@/utils/common';
import { images } from '@/assets/images';
import StatisticValue from './components/statistic-value';
import styles from './index.less';

const PAGE_SIZE = 4;

interface DataListProps
  extends Omit<
    IPolicyCardProps,
    'defaultItems' | 'insurerName' | 'type' | 'children'
  > {
  defaultItems: {
    title: string;
    info: any;
    span?: 6 | 12 | 18 | 24;
  }[];
  insurerName: string;
  type: string;
  children: string;
}

const BusinessManagementDownline = () => {
  const { formatMessage } = useIntl();
  const { isPc } = useContext(Context);
  const [renderList, setRenderList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const query = useRef(''); // 搜索的value
  const cursor = useRef('0'); // 分页游标
  const lastListLength = useRef<undefined | number>(undefined);

  const getDataList = (isScroll: boolean) => {
    if (cursor.current === '') return; // 最后一次返回为空字符串
    if (lastListLength.current && lastListLength.current < PAGE_SIZE) return; // 最后一次请求列表的长度小于规定的长度
    if (cursor.current === '0' && isScroll) return;
    setLoading(true);
    getDownlines({ cursor: cursor.current, size: PAGE_SIZE, q: query.current })
      .then((res) => {
        if (res) {
          if (res.list) {
            setRenderList((prev) => [...prev, ...res.list]);
            lastListLength.current = res.list?.length;
          }
          cursor.current = res.cursor;
        }
      })
      .finally(() => {
        window.removeEventListener(
          'scroll',
          handleOnScroll(() => getDataList(true)),
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    getDataList(false);
    window.addEventListener(
      'scroll',
      handleOnScroll(() => getDataList(true)),
    );
    return () => {
      window.removeEventListener(
        'scroll',
        handleOnScroll(() => getDataList(true)),
      );
    };
  }, []);

  const refreshList = () => {
    setLoading(true);
    getDownlines({ size: PAGE_SIZE, q: query.current })
      .then((res) => {
        if (res) {
          if (res.list) {
            setRenderList(res.list);
            lastListLength.current = res.list?.length;
          }
          cursor.current = res.cursor;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const dataIsEmpty = renderList?.length === 0;

  const dataList = renderList.map((item: any) => {
    const {
      name,
      role,
      status,
      agent_code,
      gwp,
      commission,
      policy_sold,
      display_id,
    } = item || {};
    return {
      insurerName: name,
      statusProps: {
        type: status ? UserStatusTypes[status] : '',
        children: status ? formatMessage(UserStatusMessages[status]) : '',
      },
      defaultItems: [
        {
          title: formatMessage({ id: 'Title' }),
          info: role ? formatMessage(RoleMessages[role]) : '-',
          span: 4,
        },
        {
          title: formatMessage({ id: 'ID' }),
          info: display_id,
          span: 4,
        },
        {
          title: formatMessage({ id: 'Gross Written Premium (VND)' }),
          info: <StatisticValue premium={gwp} />,
          span: 8,
        },
        {
          title: formatMessage({ id: 'Commission (VND)' }),
          info: <StatisticValue premium={commission} />,
          span: 4,
        },
        {
          title: formatMessage({ id: 'Policy Sold' }),
          info: policy_sold,
          span: 4,
        },
      ],
    } as unknown as DataListProps;
  });

  const changeFun = useCallback(() => {
    refreshList();
  }, []);

  const debounceFun = useCallback(
    debounce(changeFun, 800, {
      leading: false,
      trailing: true,
    }),
    [changeFun],
  );

  const debounceHandleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      debounceFun();
      query.current = v;
    },
    [debounceFun],
  );

  const H5DownlineCards: any = (): JSX.Element[] => {
    return renderList.map((item: any) => {
      const {
        name,
        role,
        status,
        id,
        gwp,
        commission,
        policy_sold,
        agent_code,
        display_id,
        phone_number,
      } = item || {};

      const title = role ? formatMessage(RoleMessages[role]) : '-';

      const statusType = status ? UserStatusTypes[status] : undefined;
      const statusChildren = status
        ? formatMessage(UserStatusMessages[status])
        : '';

      return (
        <div className={styles.h5DownlineCard} key={id}>
          <div className={styles.top}>
            <div className={styles.title}>
              <Typography level="body1">{name}</Typography>
              <StatusBox type={statusType}>{statusChildren}</StatusBox>
            </div>
            <div className={styles.subtitle}>
              <Typography level="body3">{title}</Typography>
              <Typography level="body3">{display_id}</Typography>
            </div>
            <a href={`tel:0${phone_number}`}>
              <PhoneOutlined className={styles.phone} />
            </a>
          </div>
          <div className={styles.bottom}>
            <div className={styles.line}>
              <div className={styles.lineLeft}>
                <img src={images.total_premium_orange_icon} alt="" />
                <Typography level="body3">
                  {formatMessage({ id: 'Gross Written Premium (VND)' })}
                </Typography>
              </div>
              <Typography level="body3">
                <StatisticValue premium={gwp} />
              </Typography>
            </div>
            <div className={styles.line}>
              <div className={styles.lineLeft}>
                <img src={images.commission_orange_icon} alt="" />
                <Typography level="body3">
                  {formatMessage({ id: 'Commission (VND)' })}
                </Typography>
              </div>
              <Typography level="body3">
                <StatisticValue premium={commission} />
              </Typography>
            </div>
            <div className={styles.line}>
              <div className={styles.lineLeft}>
                <img src={images.policy_orange_icon} alt="" />
                <Typography level="body3">
                  {formatMessage({ id: 'Policy Sold' })}
                </Typography>
              </div>
              <Typography level="body3">
                <StatisticValue premium={policy_sold} />
              </Typography>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderCards = isPc ? (
    <PolicyList dataList={dataList} />
  ) : (
    <H5DownlineCards />
  );

  return (
    <div className={styles.downline}>
      <Input
        prefix={<SearchOutlined />}
        className={styles.search}
        onChange={debounceHandleChange}
        placeholder={formatMessage({ id: 'Search' })}
      />
      <Spin spinning={loading}>
        {dataIsEmpty ? (
          <div className={styles.noDataWrap}>
            <img src={images.no_data_icon} alt="" />
            <Typography level="body1">
              {formatMessage({ id: 'There is currently no data.' })}
            </Typography>
          </div>
        ) : (
          renderCards
        )}
      </Spin>
    </div>
  );
};

export default BusinessManagementDownline;
